import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as assignmentApi from '@/apis/assignments';

// 쿼리 키 상수화
export const ASSIGNMENT_KEYS = {
    all: ['assignments'],
    byCourse: (courseId) => [...ASSIGNMENT_KEYS.all, 'byCourse', courseId],
    details: () => [...ASSIGNMENT_KEYS.all, 'detail'],
    detail: (id) => [...ASSIGNMENT_KEYS.details(), id],
};

/* ==========================================
  Queries (GET)
========================================== */

/**
 * 특정 과정의 과제 목록 조회
 * @param {number} courseId - 과정 ID
 * @returns {Object} React Query hook 객체
 */
export const useAssignmentsByCourse = (courseId) => {
    return useQuery({
        queryKey: ASSIGNMENT_KEYS.byCourse(courseId),
        queryFn: () => assignmentApi.fetchAssignmentsByCourse(courseId),
        enabled: !!courseId,
    });
};

/**
 * 과제 상세 조회
 * @param {number} assignmentId - 과제 ID
 */
export const useAssignmentDetail = (assignmentId) => {
    return useQuery({
        queryKey: ASSIGNMENT_KEYS.detail(assignmentId),
        queryFn: () => assignmentApi.fetchAssignmentDetail(assignmentId),
        enabled: !!assignmentId,
    });
};

/* ==========================================
  Mutations (CUD)
========================================== */

/**
 * 과제 생성
 */
export const useCreateAssignment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (assignmentData) => {
            const response = await assignmentApi.createAssignment(assignmentData);
            // Location 헤더에서 assignmentId 추출
            const headers = response?.headers;
            const location = headers?.location || 
                            headers?.Location || 
                            headers?.get?.('location') ||
                            headers?.get?.('Location') ||
                            headers?.['location'] ||
                            headers?.['Location'];

            let assignmentId = null;
            if (location) {
                const match = location.match(/\/(\d+)$/);
                if (match) {
                    assignmentId = parseInt(match[1]);
                }
            }

            return { success: true, assignmentId };
        },
        onSuccess: (data, variables) => {
            // 특정 과정의 과제 목록 갱신
            if (variables.courseId) {
                queryClient.invalidateQueries({ queryKey: ASSIGNMENT_KEYS.byCourse(variables.courseId) });
            }
        },
    });
};

/**
 * 과제 수정
 */
export const useUpdateAssignment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: assignmentApi.updateAssignment,
        onSuccess: (data, variables) => {
            // 상세 정보 갱신
            if (variables.assignmentId) {
                queryClient.invalidateQueries({ queryKey: ASSIGNMENT_KEYS.detail(variables.assignmentId) });
            }
            // 특정 과정의 과제 목록 갱신
            if (variables.courseId) {
                queryClient.invalidateQueries({ queryKey: ASSIGNMENT_KEYS.byCourse(variables.courseId) });
            }
        },
    });
};

/**
 * 과제 삭제
 */
export const useDeleteAssignment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: assignmentApi.deleteAssignment,
        onSuccess: (data, assignmentId) => {
            // 상세 정보 갱신
            queryClient.invalidateQueries({ queryKey: ASSIGNMENT_KEYS.detail(assignmentId) });
            // 모든 과정의 과제 목록 갱신 (courseId를 알 수 없으므로 전체 갱신)
                queryClient.invalidateQueries({ queryKey: ASSIGNMENT_KEYS.byCourse() });
        },
    });
};
